import { authService } from '@/services/authService';
import {
  getExpectedSessionUser,
  reloadIntoCurrentSession,
  tokenUserUuid,
} from '@/services/sessionIdentity';

// Base URL without any path prefixes
export const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.staging.alitahealth.ai/';

// API version and base path
export const API_VERSION = 'v1';
export const API_BASE_PATH = `api/${API_VERSION}`;

/**
 * Generic API response wrapper used across the application
 * @template T - The type of data in the response
 */
export interface ApiResponse<T = unknown> {
  status: string;
  code: number;
  data: T;
}

/**
 * Reads an error response body and produces a short, human-readable message.
 * Strips Django/HTML debug pages down to the title and falls back to a status
 * summary so we never surface raw markup to the UI or logs.
 *
 * LOSSY: when the body is JSON this collapses it to `detail`/`message`/`error`
 * and discards the rest. Callers that need the structured payload (e.g.
 * PublishBlockedError needs `invalid_multiple_choice_questions`) must read
 * `error.body` off the thrown Error — fetchWithAuth attaches the raw body
 * before invoking this. Do not add new call sites that rely on this returning
 * the full JSON; clone the response and parse the body yourself.
 */
export async function readErrorMessage(response: Response): Promise<string> {
  const status = `${response.status} ${response.statusText}`.trim();
  let body = '';
  try {
    body = await response.text();
  } catch {
    return status;
  }
  if (!body) return status;

  const trimmed = body.trim();

  if (/^[<\s]*(?:<!doctype|<html|<\?xml)/i.test(trimmed)) {
    const title = trimmed.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]?.trim();
    return title ? `${status}: ${title}` : status;
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const nestedData =
        parsed.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data)
          ? (parsed.data as Record<string, unknown>)
          : undefined;
      const message =
        (typeof parsed.detail === 'string' && parsed.detail) ||
        (typeof parsed.message === 'string' && parsed.message) ||
        (typeof parsed.error === 'string' && parsed.error) ||
        // Legacy `error()` envelope: {"data": "<human message>", "status": "failed", ...}
        (typeof parsed.data === 'string' && parsed.data) ||
        (typeof nestedData?.message === 'string' && nestedData.message) ||
        (typeof nestedData?.error === 'string' && nestedData.error) ||
        (typeof nestedData?.code === 'string' && nestedData.code);
      if (message) return `${status}: ${message}`;
    } catch {
      // Fall through to truncated text below
    }
  }

  const flat = trimmed.replace(/\s+/g, ' ');
  return flat.length > 200 ? `${status}: ${flat.slice(0, 200)}…` : `${status}: ${flat}`;
}

/**
 * Constructs a full API URL by combining the base URL with the API path
 */
export function getApiUrl(path: string): string {
  // Remove any leading slashes to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  // Ensure there's exactly one slash between base URL and path
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
  return `${baseUrl}/${API_BASE_PATH}/${cleanPath}`;
}

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let refreshPromise: Promise<{
  access_token: string;
  refresh_token?: string;
} | null> | null = null;
// Track last refresh time to prevent excessive refresh attempts
let lastRefreshTime: number | null = null;
const REFRESH_COOLDOWN_MS = 60 * 1000; // 1 minute
const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

/**
 * First path segments of the routes anonymous end users land on from a texted
 * or shared link: the Link Agent / SMS secure page (/a), the chat-bubble embed
 * (/agent), the short-link redirect (/s), and the meeting reschedule page
 * (/reschedule). On these surfaces the link token or session id is the
 * credential — never a Hub JWT (ALI-2310).
 *
 * Matching is by whole segment, not prefix: /agents/... is a Hub route.
 */
const PUBLIC_SURFACE_SEGMENTS = new Set(['a', 'agent', 's', 'reschedule']);

/**
 * True when the current page is a public visitor surface. There, fetchWithAuth
 * behaves anonymously: no Authorization header, no token refresh, and never a
 * redirect to /login. A Hub auth cookie is domain-wide, so a visitor (or an
 * operator testing their own agent) can reach these pages carrying a stale
 * token; attaching it gets a 401 back even from AllowAny endpoints, and the
 * default 401 handling would then bounce a public visitor to the Hub login
 * page (ALI-2310).
 *
 * /preview is two pages in one, split by query rather than path. With ?t= it
 * is a draft's secure-link page — minted by the backend exactly like /a/?t=
 * and just as shareable, so it gets the same anonymous treatment. (The draft
 * token's claim is the one call that wants the editor's credentials, and
 * smsClient.claimSecureSession attaches them itself, outside fetchWithAuth.)
 * With ?agentId= it is the editor's own preview tab, whose data endpoint
 * (agents/preview/data/) requires the Hub session — that form must keep
 * authenticated behavior or the tab breaks.
 */
export function isPublicAgentSurface(): boolean {
  if (typeof window === 'undefined') return false;
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
  if (firstSegment === 'preview') {
    return new URLSearchParams(window.location.search).has('t');
  }
  return PUBLIC_SURFACE_SEGMENTS.has(firstSegment);
}

/**
 * Attempts to refresh the access token using the refresh token
 */
async function attemptTokenRefresh(): Promise<{
  access_token: string;
  refresh_token?: string;
} | null> {
  // If already refreshing, wait for the existing refresh attempt
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = authService.getRefreshToken();
      if (!refreshToken) {
        return null;
      }

      // Inline refresh call (avoid importing `apiClient` to prevent circular deps)
      const response = await fetch(getApiUrl(`/user/token/refresh/`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to refresh access token: ${errorText}`);
      }

      const data = (await response.json()) as Record<string, unknown>;

      const access_token =
        (data?.access_token as string | undefined) ||
        ((data?.data as { access_token?: string } | undefined)?.access_token as
          | string
          | undefined) ||
        (data?.access as string | undefined) ||
        ((data?.data as { access?: string } | undefined)?.access as string | undefined);

      if (!access_token) {
        throw new Error('No access token received from refresh endpoint');
      }

      const refresh_token =
        (data?.refresh_token as string | undefined) ||
        ((data?.data as { refresh_token?: string } | undefined)?.refresh_token as
          | string
          | undefined) ||
        (data?.refresh as string | undefined) ||
        ((data?.data as { refresh?: string } | undefined)?.refresh as string | undefined);

      const refreshResult: { access_token: string; refresh_token?: string } = {
        access_token,
        ...(refresh_token ? { refresh_token } : {}),
      };

      if (refreshResult.access_token) {
        authService.setToken(refreshResult.access_token);
        if (refreshResult.refresh_token) authService.setRefreshToken(refreshResult.refresh_token);
        lastRefreshTime = Date.now();
        return refreshResult;
      }

      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Never send a request as a different user than this tab displays.
 *
 * Auth tokens are host-wide cookies while the rendered user is per-tab state,
 * so a login in another tab swaps the credentials under us — a save here would
 * then silently write to the other account (how notification preferences got
 * saved to the wrong user). On a positive mismatch, reload the tab into the
 * browser's real session instead of sending the request. Tabs with no user yet
 * (bootstrap, login page) and unparseable tokens pass — the guard only fires
 * when the token verifiably belongs to someone other than the displayed user.
 */
function assertTokenMatchesDisplayedUser(token: string | null | undefined): void {
  if (!token) return;
  const displayed = getExpectedSessionUser();
  if (!displayed) return;
  const actual = tokenUserUuid(token);
  if (!actual || actual === displayed) return;
  console.error(
    'Aborting API call: the browser session no longer belongs to the user this tab is displaying ' +
      '(another tab logged into a different account). Reloading into the current session.'
  );
  reloadIntoCurrentSession();
  throw new Error('Session user changed; request aborted');
}

/**
 * Redirects to login page with return URL saved
 */
function redirectToLogin() {
  if (typeof window !== 'undefined') {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const returnPath =
      currentPath && !currentPath.startsWith('/login') && !currentPath.startsWith('//')
        ? currentPath
        : '/home/';

    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnPath);
    } catch {
      // Ignore storage errors; redirect still proceeds with query param.
    }

    window.location.href = `/login/?redirect=${encodeURIComponent(returnPath)}`;
  } else {
    // If "window" is undefined (e.g., on the server side), do nothing or log (no redirect possible)
    // Optionally, you might want to log for debugging:
    // console.warn("redirectToLogin called on the server; cannot redirect.");
  }
}

const MFA_GATE_CODES = new Set(['mfa_enrollment_required', 'mfa_verification_required']);

/**
 * Whether a 401 is the server's MFA gate (ALI-2212) rather than a stale token.
 * Refreshing cannot cure these: the account must set up MFA (enrollment_required)
 * or the session never passed MFA at all (verification_required), so the only
 * useful move is a clean logout to the login screen, where the MFA step takes over.
 */
async function isMfaGated(response: Response): Promise<boolean> {
  try {
    const body = await response.clone().json();
    return MFA_GATE_CODES.has(body?.code);
  } catch {
    return false;
  }
}

/**
 * Fetches data from the API without requiring authentication
 * Does not redirect on 401 errors - useful for public routes
 */
export async function fetchPublic(endpoint: string, options: RequestInit = {}) {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Only set Content-Type if not sending FormData
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const url = getApiUrl(endpoint);

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    console.error(`API Error: ${message}`);
    throw new Error(`API call failed: ${message}`);
  }

  return response.json();
}

async function parseJsonOrText(response: Response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/**
 * Fetches data from the API without requiring authentication
 * Returns parsed JSON even for non-2xx responses (useful for validation endpoints that return 400 with a JSON body)
 */
export async function fetchPublicAllowErrors(endpoint: string, options: RequestInit = {}) {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Only set Content-Type if not sending FormData
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const url = getApiUrl(endpoint);

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return parseJsonOrText(response);
}

/**
 * Fetches data from the API (client-side, no authService for static export)
 * Automatically handles token refresh on 401 responses and proactively refreshes before API calls
 */
export async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {},
  config: { allowNonOk?: boolean; quietErrors?: boolean; redirectOnAuthFailure?: boolean } = {}
) {
  // Don't attempt refresh for the refresh token endpoint itself
  const isRefreshEndpoint =
    endpoint.includes('token/refresh') || endpoint.includes('/user/token/refresh');

  // Public visitor surfaces are always anonymous: the session id / link token is
  // the credential, so a Hub token (possibly stale, and domain-wide via cookie)
  // must not ride along, and a 401 is handled locally instead of redirecting a
  // visitor to the Hub login page (ALI-2310).
  const onPublicSurface = isPublicAgentSurface();

  // Proactively refresh token before making API call if needed
  if (!isRefreshEndpoint && !onPublicSurface) {
    const refreshToken = authService.getRefreshToken();
    const shouldRefresh =
      refreshToken && (!lastRefreshTime || Date.now() - lastRefreshTime > REFRESH_COOLDOWN_MS);

    if (shouldRefresh) {
      // Attempt proactive refresh, but don't fail if it doesn't work
      // The 401 handler will catch it as a fallback
      await attemptTokenRefresh().catch(() => {
        // Silently fail - we'll retry on 401 if needed
      });
    }
  }

  // Get token after potential refresh
  const token = onPublicSurface ? null : authService.getToken();
  assertTokenMatchesDisplayedUser(token);

  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.headers as Record<string, string>),
  };
  // Only set Content-Type if not sending FormData
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const url = getApiUrl(endpoint);

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - token expired (fallback if proactive refresh didn't work)
  if (response.status === 401) {
    // No refresh for the refresh endpoint itself; no refresh-and-redirect dance
    // on public surfaces either — an anonymous visitor has nothing to refresh,
    // and must never land on the Hub login page.
    if (isRefreshEndpoint || onPublicSurface) {
      const message = await readErrorMessage(response);
      if (!config.quietErrors) {
        console.error(`API Error: ${message}`);
      }
      throw new Error(`API call failed: ${message}`);
    }

    // The MFA gate refuses the session, not the token — skip the refresh dance
    // and clear the session so the login screen's MFA step takes over.
    if (await isMfaGated(response)) {
      authService.clearTokens();
      if (config.redirectOnAuthFailure !== false) {
        redirectToLogin();
      }
      throw new Error('Authentication failed: MFA is required.');
    }

    // Attempt to refresh the token
    const refreshResult = await attemptTokenRefresh();

    if (refreshResult && refreshResult.access_token) {
      // The shared refresh cookie may belong to another account by now.
      assertTokenMatchesDisplayedUser(refreshResult.access_token);
      // Retry the original request with the new token
      const newHeaders: Record<string, string> = {
        Authorization: `Bearer ${refreshResult.access_token}`,
        ...(options.headers as Record<string, string>),
      };
      if (!(options.body instanceof FormData)) {
        newHeaders['Content-Type'] = 'application/json';
      }

      response = await fetch(url, {
        ...options,
        headers: newHeaders,
      });

      // If still 401 after refresh, refresh token is expired or the endpoint's
      // downstream auth failed. Most app calls should send the user back to login;
      // non-critical/public-adjacent calls can opt out and handle the error locally.
      if (response.status === 401) {
        if (config.redirectOnAuthFailure !== false) {
          redirectToLogin();
        }
        const message = await readErrorMessage(response);
        throw new Error(`Authentication failed: ${message}`);
      }
    } else {
      // Refresh token is expired or invalid, redirect to login unless the call opts
      // out and handles auth failure locally.
      if (config.redirectOnAuthFailure !== false) {
        redirectToLogin();
      }
      const message = await readErrorMessage(response);
      throw new Error(`Authentication failed: ${message}`);
    }
  }

  if (!response.ok) {
    if (config.allowNonOk) {
      return parseJsonOrText(response);
    }
    // Capture the raw body before `readErrorMessage` consumes it so callers
    // can recover structured payloads (e.g. PublishBlockedError needs the
    // `invalid_multiple_choice_questions` array that the friendly summary
    // strips out).
    const body = await response
      .clone()
      .text()
      .catch(() => '');
    const message = await readErrorMessage(response);
    if (!config.quietErrors) {
      console.error(`API Error: ${message}`);
    }
    const err: Error & { body?: string; status?: number } = new Error(
      `API call failed: ${message}`
    );
    err.body = body;
    err.status = response.status;
    throw err;
  }

  // Handle 204 No Content specifically
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  // If no content-type or not json, try to parse as text but might fail if empty
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return text;
  }
}

/**
 * Fetches a Blob payload with auth
 */
export async function fetchBlobWithAuth(
  endpoint: string,
  options: RequestInit = {},
  config: { allowNonOk?: boolean } = {}
): Promise<Blob> {
  // Don't attempt refresh for the refresh token endpoint itself
  const isRefreshEndpoint =
    endpoint.includes('token/refresh') || endpoint.includes('/user/token/refresh');

  // Anonymous on public visitor surfaces — see fetchWithAuth (ALI-2310).
  const onPublicSurface = isPublicAgentSurface();

  // Proactively refresh token before making API call if needed
  if (!isRefreshEndpoint && !onPublicSurface) {
    const refreshToken = authService.getRefreshToken();
    const shouldRefresh =
      refreshToken && (!lastRefreshTime || Date.now() - lastRefreshTime > REFRESH_COOLDOWN_MS);

    if (shouldRefresh) {
      await attemptTokenRefresh().catch(() => {
        // Silently fail - we'll retry on 401 if needed
      });
    }
  }

  // Get token after potential refresh
  const token = onPublicSurface ? null : authService.getToken();
  assertTokenMatchesDisplayedUser(token);

  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.headers as Record<string, string>),
  };

  const url = getApiUrl(endpoint);

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    if (isRefreshEndpoint || onPublicSurface) {
      const message = await readErrorMessage(response);
      console.error(`API Error: ${message}`);
      throw new Error(`API call failed: ${message}`);
    }

    // See fetchWithAuth: the MFA gate cannot be refreshed away.
    if (await isMfaGated(response)) {
      authService.clearTokens();
      redirectToLogin();
      throw new Error('Authentication failed: MFA is required.');
    }

    const refreshResult = await attemptTokenRefresh();

    if (refreshResult?.access_token) {
      // The shared refresh cookie may belong to another account by now.
      assertTokenMatchesDisplayedUser(refreshResult.access_token);
      const newHeaders: Record<string, string> = {
        Authorization: `Bearer ${refreshResult.access_token}`,
        ...(options.headers as Record<string, string>),
      };

      response = await fetch(url, {
        ...options,
        headers: newHeaders,
      });

      if (response.status === 401) {
        redirectToLogin();
        const message = await readErrorMessage(response);
        throw new Error(`Authentication failed: ${message}`);
      }
    } else {
      redirectToLogin();
      const message = await readErrorMessage(response);
      throw new Error(`Authentication failed: ${message}`);
    }
  }

  if (!response.ok) {
    if (config.allowNonOk) {
      return response.blob();
    }
    const message = await readErrorMessage(response);
    console.error(`API Error: ${message}`);
    throw new Error(`API call failed: ${message}`);
  }

  return response.blob();
}
